import { useCallback } from "react";
import { getProductDemand } from "@/api/client";
import type { ProductDemand } from "@/api/types";
import { CsvDownloadButton } from "@/components/CsvDownloadButton";
import { DataTable, type Column } from "@/components/DataTable";
import { MultiFilterSelect } from "@/components/MultiFilterSelect";
import { HorizontalBarChart } from "@/components/HorizontalBarChart";
import { StatCard } from "@/components/StatCard";
import { TimeSeriesAreaChart } from "@/components/TimeSeriesAreaChart";
import { ChartSkeleton, ErrorState, TableSkeleton } from "@/components/states";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useApiData } from "@/hooks/useApiData";
import { useProductFilterOptions } from "@/hooks/useProductFilters";
import { useDebouncedUrlFilter, useUrlListFilter } from "@/hooks/useUrlFilters";
import { formatCompact, formatNumber } from "@/lib/format";

type ProductRow = ProductDemand["products"][number];
type StoreRow = ProductDemand["byStore"][number];

const productColumns: Column<ProductRow>[] = [
  { header: "UPC", cell: (r) => r.upc, sortValue: (r) => r.upc },
  {
    header: "Product name",
    cell: (r) => r.productName ?? "—",
    sortValue: (r) => r.productName,
  },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

const storeColumns: Column<StoreRow>[] = [
  { header: "Store", cell: (r) => r.storeName, sortValue: (r) => r.storeName },
  {
    header: "Units",
    align: "right",
    cell: (r) => formatNumber(r.units),
    sortValue: (r) => r.units,
  },
];

export function ProductDemandPage() {
  const filters = useProductFilterOptions();
  const [draft, setDraft, q] = useDebouncedUrlFilter("q");
  const [storeIds, setStoreIds, store] = useUrlListFilter("store");
  const [brandIds, setBrandIds, brand] = useUrlListFilter("brand");
  const [categoryIds, setCategoryIds, category] = useUrlListFilter("category");
  const [subcategoryIds, setSubcategoryIds, subcategory] =
    useUrlListFilter("subcategory");

  const fetchDemand = useCallback(
    (token: string) =>
      getProductDemand(token, {
        q: q || undefined,
        storeId: store,
        brandId: brand,
        categoryId: category,
        subcategoryId: subcategory,
      }),
    [q, store, brand, category, subcategory],
  );
  const state = useApiData(fetchDemand);
  const data = state.status === "ready" ? state.data : null;

  const totalUnits = data
    ? data.unitsByDay.reduce((sum, d) => sum + d.units, 0)
    : null;

  return (
    <div className="flex flex-col gap-6" aria-busy={state.status === "loading"}>
      <div>
        <h1 className="text-xl font-semibold">Product demand</h1>
        <p className="text-sm text-muted-foreground">
          What retailers sold in the last 30 days.
        </p>
      </div>

      <div className="flex flex-wrap gap-4">
        <div className="flex flex-col gap-1">
          <label
            htmlFor="demand-search"
            className="text-xs font-medium text-muted-foreground"
          >
            Product name
          </label>
          <input
            id="demand-search"
            type="search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. Fanta"
            className="h-9 w-64 max-w-full rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>
        <MultiFilterSelect
          label="Store"
          allLabel="All stores"
          values={storeIds}
          onChange={setStoreIds}
          disabled={filters.disabled}
          options={filters.storeOptions}
        />
        <MultiFilterSelect
          label="Brand"
          allLabel="All brands"
          values={brandIds}
          onChange={setBrandIds}
          disabled={filters.disabled}
          options={filters.brandOptions}
        />
        <MultiFilterSelect
          label="Category"
          allLabel="All categories"
          values={categoryIds}
          onChange={setCategoryIds}
          disabled={filters.disabled}
          options={filters.level1CategoryOptions}
        />
        <MultiFilterSelect
          label="Subcategory"
          allLabel="All subcategories"
          values={subcategoryIds}
          onChange={setSubcategoryIds}
          disabled={filters.disabled}
          options={filters.categoryOptions}
        />
      </div>

      {state.status === "error" ? (
        <ErrorState message={state.message} onRetry={state.reload} />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Total units"
              value={totalUnits === null ? null : formatCompact(totalUnits)}
            />
            <StatCard
              label="Stores with sales"
              value={data && formatNumber(data.byStore.length)}
            />
            <StatCard
              label="Products matched"
              value={
                data &&
                `${formatNumber(data.products.length)}${data.capped ? "+" : ""}`
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Units over time</CardTitle>
              <CardDescription>Daily units</CardDescription>
            </CardHeader>
            <CardContent>
              {!data ? (
                <ChartSkeleton height={260} />
              ) : (
                <TimeSeriesAreaChart
                  data={data.unitsByDay.map((d) => ({
                    day: d.day,
                    value: d.units,
                  }))}
                  seriesName="Units"
                />
              )}
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1.5">
                  <CardTitle>Top products</CardTitle>
                  {data?.capped ? (
                    <CardDescription>
                      Showing the top {formatNumber(data.productLimit)} by
                      units — narrow the search to see the rest.
                    </CardDescription>
                  ) : null}
                </div>
                {data ? (
                  <CsvDownloadButton
                    name="product-demand"
                    columns={productColumns}
                    rows={data.products}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {data ? (
                  <DataTable
                    columns={productColumns}
                    rows={data.products}
                    rowKey={(r) => r.upc}
                    maxHeight="28rem"
                    emptyMessage="No sales match"
                  />
                ) : (
                  <TableSkeleton />
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                <CardTitle>Units by store</CardTitle>
                {data ? (
                  <CsvDownloadButton
                    name="product-demand-by-store"
                    columns={storeColumns}
                    rows={data.byStore}
                  />
                ) : null}
              </CardHeader>
              <CardContent>
                {data ? (
                  <DataTable
                    columns={storeColumns}
                    rows={data.byStore}
                    rowKey={(r) => r.storeName}
                    maxHeight="28rem"
                    emptyMessage="No sales match"
                  />
                ) : (
                  <TableSkeleton />
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Units by DMA</CardTitle>
            </CardHeader>
            <CardContent>
              {data ? (
                <HorizontalBarChart
                  data={data.byDma.map((d) => ({
                    name: d.dma,
                    value: d.units,
                  }))}
                  seriesName="Units"
                />
              ) : (
                <ChartSkeleton />
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
